import { Edit as EditIcon } from "@mui/icons-material";
import DescriptionIcon from "@mui/icons-material/Description";
import EuroIcon from "@mui/icons-material/Euro";
import PolicyIcon from "@mui/icons-material/Policy";
import { Box, Card, CardContent, Divider, Grid, IconButton, LinearProgress, Stack, Typography } from "@mui/material";
import { isNil } from "lodash";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../auth/AuthContext";
import { AssociatedFilesView } from "../components/associatedFiles/AssociatedFilesView";
import type { Product } from "../components/productsTable/ProductsTableNew";
import ProductsTable from "../components/productsTable/ProductsTableNew";
import type { Currency } from "../data/BaseEntity";
import dayjs from "../dayjs";
import { Price } from "../misc/price";
import CreateCalculationButton from "../orders/CreateCalculationButton";
import { useCancellationCondition } from "../orders/cancellation-conditions/useCancellationCondition";
import OrderInvoices from "../orders/invoices/OrderInvoices";
import Confirmations from "../orders/orderConfirmation/Confirmations";
import ListTemplates from "../orders/orderEdit/ListTemplates";
import { sumPayments } from "../orders/orderEdit/OrderPayments";
import { getTotalPrice } from "../orders/orderEdit/getTotalPrice";
import { useSavedOrderToProducts } from "../orders/orderEdit/orderToProducts";
import OrderStatusView2 from "../orders/orderStatus/OrderStatusView2";
import DownloadParticipantListTemplateButton from "../orders/participantLists/DownloadParticipantListTemplateButton";
import { getParticipantListTemplateXlsx } from "../orders/participantLists/participantListApi";
import { useOrder } from "../orders/useOrder";
import SupervisorInfo from "../orders2/SupervisorInfo";
import BackToOrderListButton from "./BackToOrderListButton";
import { CustomerOrderNameDialog, type CustomerOrderNameData } from "./CustomerOrderNameDialog";
import DownloadOrderPdfButton from "./DownloadOrderPdfButton";
import GoToParticipantListButton from "./GoToParticipantListButton";
import SendCheckInButton from "./emails/SendCheckInButton";

export const moneyText = (value: number | null, currency: Currency): string =>
  isNil(value) ? `n.d.` : `${value} ${currency}`;

const CustomerOrderPage = () => {
  const navigate = useNavigate();
  const { order, isLoading: isOrderLoading, onUpdateContactPerson, onUpdateOrderName } = useOrder();
  const { products, isLoading: areProductsLoading } = useSavedOrderToProducts(order);
  const { cancellationCondition, isLoading: isCondtionLoading } = useCancellationCondition(
    order?.cancellationConditionId || null,
  );
  const { context } = useAuthContext();
  const [customerOrderNameData, setCustomerOrderNameData] = useState<CustomerOrderNameData>();

  const isLoading = isOrderLoading || areProductsLoading || isCondtionLoading;

  const canCreateCalculationFromProducts =
    context?.role === "CUSTOMER" && context?.customPrivileges.canCreateCalculationFromProducts;

  if (isLoading) {
    return (
      <Grid container>
        <Grid size={12}>
          <LinearProgress data-testid="loading-indicator" />
        </Grid>
      </Grid>
    );
  }

  if (!order) {
    return (
      <Grid
        container
        sx={{
          marginTop: 2,
          marginBottom: 2,
        }}
      >
        <Typography variant="h5">Takie zamówienie nie istnieje</Typography>
      </Grid>
    );
  }
  const totalPrice = getTotalPrice(
    order.products.map(({ price, cancelled }) => ({ price: new Price(price), cancelled })),
  );
  const paymentsSum = sumPayments(order.payments);
  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        sx={{
          mb: 4,
          justifyContent: "space-between",
          // Zachowanie podobne do headera w podglądzie/edycji kalkulacji:
          alignItems: { xs: "flex-start", md: "center" },
          position: { xs: "static", md: "sticky" },
          top: { xs: 56, md: 128 },
          zIndex: 10,
          bgcolor: "background.default",
          py: 2,
          mt: 0,
          borderBottom: "1px solid rgba(0,0,0,0.05)",
        }}
      >
        <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
          <BackToOrderListButton />
          <Box>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                Zamówienie #{order.userFriendlyId}
              </Typography>
              <OrderStatusView2 value={order.status} size="medium" />
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5, alignItems: "center" }}>
              <Typography variant="body1" color="text.secondary">
                {order.created ? `${dayjs(order.created).format("DD.MM.YYYY")} • ${order.name}` : order.name}
              </Typography>
              <IconButton
                size="small"
                aria-label="Edytuj koordynatora wyjazdu"
                onClick={() => setCustomerOrderNameData({ value: order.name })}
                sx={{
                  p: 0.25,
                  color: "text.secondary",
                  "&:hover": {
                    color: "primary.main",
                  },
                }}
              >
                <EditIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Stack>
          </Box>
        </Stack>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 2, width: { xs: "100%", sm: "auto" } }}>
          <GoToParticipantListButton order={order} onClick={() => navigate(`/orders/${order.id}/participant-lists`)} />
          <DownloadParticipantListTemplateButton
            buttonText="Pobierz szablon listy"
            filename={`${order.userFriendlyId} - szablon listy.xlsx`}
            getFile={() => getParticipantListTemplateXlsx(order.id)}
          />
          <SendCheckInButton orderId={order.id} />
          <DownloadOrderPdfButton orderId={order.id} orderType={order.type} filename={`${order.name}.pdf`} />
          {canCreateCalculationFromProducts && (
            <CreateCalculationButton onClick={() => navigate(`/calculations/new?fromOrder=${order.id}`)} />
          )}
        </Stack>
      </Stack>

      <Stack spacing={3} sx={{ mb: 4 }}>
        {/* Górny rząd: 50/50 (Dane / Finanse) */}
        <Stack direction={{ xs: "column", md: "row" }} spacing={3} sx={{ alignItems: "stretch" }}>
          <SupervisorInfo
            supervisorId={order.supervisorId}
            customerId={order.customerId}
            order={{
              id: order.userFriendlyId,
              name: order.name,
              startDate: order.start ?? "",
              customerContactPersonEmail: order.customerContactPersonEmail,
            }}
            onUpdateContactPerson={onUpdateContactPerson}
          />
          <Box sx={{ width: { xs: "100%", md: "50%" }, display: "flex" }}>
            <Card sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
              <CardContent>
                <Stack direction="row" spacing={1} sx={{ mb: 2, color: "text.secondary", alignItems: "center" }}>
                  <EuroIcon fontSize="small" />
                  <Typography variant="subtitle2" sx={{ textTransform: "uppercase" }}>
                    Rozliczenia
                  </Typography>
                </Stack>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography variant="caption" color="text.secondary">
                      Zaliczka
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: "500" }}>
                      {moneyText(order.advance?.amount || null, order.currency)}
                    </Typography>
                    <Typography variant="caption" color="error">
                      Do: {order.advance?.deadline}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 4 }}>
                    <Typography variant="caption" color="text.secondary">
                      Liczba osób
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: "500" }}>
                      {order.participantsNumber.numberOfPaid}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 4 }}>
                    <Typography variant="caption" color="text.secondary">
                      Osoby bezpłatne
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: "500" }}>
                      {order.participantsNumber.numberOfStaff || 0}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Stack direction="row" sx={{ justifyContent: "space-between" }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Suma wpłat
                    </Typography>
                    <Typography variant="body1" color="success" sx={{ fontWeight: "bold" }}>
                      {moneyText(paymentsSum, order.currency)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography variant="caption" color="text.secondary">
                      Pozostało do zapłaty
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                      {moneyText(
                        totalPrice.isPresent() ? (totalPrice.subtract(paymentsSum).value as number) : null,
                        order.currency,
                      )}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </Stack>
        <Stack direction={{ xs: "column", md: "row" }} spacing={3} sx={{ alignItems: "stretch" }}>
          <Box sx={{ width: { xs: "100%", md: "50%" }, display: "flex" }}>
            <Card sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
              <CardContent>
                <Stack direction="row" spacing={1} sx={{ mb: 2, color: "text.secondary", alignItems: "center" }}>
                  <PolicyIcon fontSize="small" />
                  <Typography variant="subtitle2" sx={{ textTransform: "uppercase" }}>
                    Warunki anulacji
                  </Typography>
                </Stack>
                <Typography sx={{ whiteSpace: "pre-line" }}>{cancellationCondition?.text}</Typography>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ width: { xs: "100%", md: "50%" }, display: "flex" }}>
            <Card sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
              <CardContent>
                <Stack direction="row" spacing={1} sx={{ mb: 2, color: "text.secondary", alignItems: "center" }}>
                  <DescriptionIcon fontSize="small" />
                  <Typography variant="subtitle2" sx={{ textTransform: "uppercase" }}>
                    Notatka
                  </Typography>
                </Stack>
                <Typography>{order.info}</Typography>
              </CardContent>
            </Card>
          </Box>
        </Stack>
        <Box>
          <Card sx={{ width: "100%" }}>
            <CardContent>
              <Stack direction="row" spacing={1} sx={{ mb: 2, color: "text.secondary", alignItems: "center" }}>
                <DescriptionIcon fontSize="small" />
                <Typography variant="subtitle2" sx={{ textTransform: "uppercase" }}>
                  Szablony i załączniki
                </Typography>
              </Stack>
              <Grid size={12}>
                <Stack direction="column" spacing={1}>
                  <AssociatedFilesView values={order.files} groupByVisibility />
                  <OrderInvoices order={order} readonly />
                  <Confirmations orderId={order.id} readonly />
                  <ListTemplates orderId={order.id} />
                </Stack>
              </Grid>
            </CardContent>
          </Card>
        </Box>
      </Stack>
      <Typography variant="h6" sx={{ mb: 4, mt: 2, fontWeight: "bold" }}>
        Produkty
      </Typography>
      <ProductsTable
        values={products as Product[]}
        currency={order.currency}
        totalPrice={totalPrice}
        hideProductPrices={order.type === "PACKAGE"}
        orderId={order.id}
        files={order.files}
      />
      {customerOrderNameData && (
        <CustomerOrderNameDialog
          data={customerOrderNameData}
          onUpdate={onUpdateOrderName}
          onClose={() => setCustomerOrderNameData(undefined)}
        />
      )}
    </Box>
  );
};

export default CustomerOrderPage;
